import { authService } from './authService';

const API_BASE_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:3000';

/**
 * Payment Provider Service - API calls for managing payment providers
 */
export const paymentProviderService = {
    /**
     * Get all payment providers
     */
    async getAll() {
        try {
            const response = await fetch(`${API_BASE_URL}/payment-providers`, {
                method: 'GET',
                headers: authService.getAuthHeaders()
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to get payment providers');
            return data;
        } catch (error) {
            console.error('Error getting payment providers:', error);
            throw error;
        }
    },

    /**
     * Get payment providers for select dropdown
     */
    async getSelectOptions() {
        try {
            const response = await fetch(`${API_BASE_URL}/payment-providers/select`, {
                method: 'GET',
                headers: authService.getAuthHeaders()
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to get provider options');
            return data;
        } catch (error) {
            console.error('Error getting provider options:', error);
            throw error;
        }
    },

    /**
     * Get single payment provider by ID
     */
    async getById(providerId) {
        try {
            const response = await fetch(`${API_BASE_URL}/payment-providers/${providerId}`, {
                method: 'GET',
                headers: authService.getAuthHeaders()
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to get payment provider');
            return data;
        } catch (error) {
            console.error('Error getting payment provider:', error);
            throw error;
        }
    },

    /**
     * Get payment provider by code
     */
    async getByCode(code) {
        try {
            const response = await fetch(`${API_BASE_URL}/payment-providers/code/${code}`, {
                method: 'GET',
                headers: authService.getAuthHeaders()
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to get payment provider');
            return data;
        } catch (error) {
            console.error('Error getting payment provider by code:', error);
            throw error;
        }
    },

    /**
     * Create new payment provider
     */
    async create(providerData) {
        try {
            const response = await fetch(`${API_BASE_URL}/payment-providers`, {
                method: 'POST',
                headers: {
                    ...authService.getAuthHeaders(),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(providerData)
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to create payment provider');
            return data;
        } catch (error) {
            console.error('Error creating payment provider:', error);
            throw error;
        }
    },

    /**
     * Update payment provider
     */
    async update(providerId, providerData) {
        try {
            const response = await fetch(`${API_BASE_URL}/payment-providers/${providerId}`, {
                method: 'PUT',
                headers: {
                    ...authService.getAuthHeaders(),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(providerData)
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to update payment provider');
            return data;
        } catch (error) {
            console.error('Error updating payment provider:', error);
            throw error;
        }
    },

    /**
     * Link bank account to provider
     */
    async linkBankAccount(providerId, bankAccountId) {
        try {
            const response = await fetch(`${API_BASE_URL}/payment-providers/${providerId}/link-bank`, {
                method: 'POST',
                headers: {
                    ...authService.getAuthHeaders(),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ bank_account_id: bankAccountId })
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to link bank account');
            return data;
        } catch (error) {
            console.error('Error linking bank account:', error);
            throw error;
        }
    },

    /**
     * Calculate fee for amount
     */
    async calculateFee(providerId, amount) {
        try {
            const response = await fetch(`${API_BASE_URL}/payment-providers/${providerId}/calculate-fee`, {
                method: 'POST',
                headers: {
                    ...authService.getAuthHeaders(),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ amount })
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to calculate fee');
            return data;
        } catch (error) {
            console.error('Error calculating fee:', error);
            throw error;
        }
    },

    /**
     * Delete (deactivate) payment provider
     */
    async delete(providerId) {
        try {
            const response = await fetch(`${API_BASE_URL}/payment-providers/${providerId}`, {
                method: 'DELETE',
                headers: authService.getAuthHeaders()
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to delete payment provider');
            return data;
        } catch (error) {
            console.error('Error deleting payment provider:', error);
            throw error;
        }
    }
};
